import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FolderOpen, Settings, HardDrive, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '@/features/auth';

export function WorkspaceDropdown() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = (() => {
    if (!user) return '';
    if (i18n.language === 'he' && user.firstNameHe) {
      return `${user.firstNameHe} ${user.lastNameHe || user.lastName}`;
    }
    return `${user.firstName} ${user.lastName}`;
  })();

  const initial = (user?.firstName?.[0] ?? 'U').toUpperCase();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2.5 px-2 py-2 rounded-lg transition-colors hover:bg-[var(--user-nav-hover-bg)]"
      >
        {/* Initial avatar */}
        <div
          className="flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-bold text-white flex-shrink-0"
          style={{ backgroundColor: '#5865F2' }}
        >
          {initial}
        </div>
        <span className="flex-1 text-start text-[13px] font-semibold truncate" style={{ color: 'var(--user-page-heading)' }}>
          {t('editor.workspaceTitle', "{{name}}'s space", { name: displayName })}
        </span>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--user-page-text)', opacity: 0.4 }}
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-50 mt-1 rounded-xl border shadow-xl overflow-hidden"
            style={{
              backgroundColor: 'var(--user-page-card-bg)',
              borderColor: 'var(--user-page-card-border)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            }}
          >
            <div className="py-1">
              <DropdownItem
                icon={<FolderOpen size={15} />}
                label={t('editor.allProjects', 'All projects')}
                onClick={() => { navigate('/studio'); setOpen(false); }}
              />
              <DropdownItem
                icon={<HardDrive size={15} />}
                label={t('editor.storageUsage', 'Storage')}
                onClick={() => setOpen(false)}
                disabled
              />
              <DropdownItem
                icon={<Trash2 size={15} />}
                label={t('editor.trash', 'Trash')}
                onClick={() => setOpen(false)}
                disabled
              />
              <div className="mx-2 my-1 h-px" style={{ backgroundColor: 'var(--user-page-card-border)' }} />
              <DropdownItem
                icon={<Settings size={15} />}
                label={t('editor.workspaceSettings', 'Workspace settings')}
                onClick={() => setOpen(false)}
                disabled
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DropdownItem({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-start transition-colors hover:bg-[var(--user-nav-hover-bg)] disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent"
      style={{ color: 'var(--user-page-text)' }}
    >
      <span className="flex-shrink-0 opacity-60">{icon}</span>
      <span className="text-[13px] font-medium">{label}</span>
    </button>
  );
}
